import { useEffect, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { startSessionHeartbeat, onSessionExpired } from './lib/api';

// Code-splitting por ruta (React.lazy): CRA mete TODO en un main.js único, así que las
// páginas públicas embebidas en el storefront (/vende en un iframe, /tasa, /objetivos) se
// tragaban el bundle entero del Dashboard de Ops (~2 MB) → 3-5 s en blanco en móvil 4G.
// Partiendo cada ruta en su chunk, /vende sólo baja el shell + su propio código.
// Named exports (SellForm/Tasador/LensCompat) → se mapean a `default` para lazy().
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MarketAnalysisDashboard = lazy(() => import('./pages/MarketAnalysisDashboard'));
const AnalogCamerasDashboard = lazy(() => import('./pages/AnalogCamerasDashboard'));
const GoogleAdsDashboard = lazy(() => import('./pages/GoogleAdsDashboard'));
const SellForm = lazy(() => import('./pages/SellForm').then((m) => ({ default: m.SellForm })));
const Tasador = lazy(() => import('./pages/Tasador').then((m) => ({ default: m.Tasador })));
const LensCompat = lazy(() => import('./pages/LensCompat').then((m) => ({ default: m.LensCompat })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,        // 1 min — los KPIs no cambian cada segundo
      gcTime: 5 * 60 * 1000,       // 5 min en caché tras dejar de usarse
      refetchOnWindowFocus: false, // No refetch al cambiar de pestaña del navegador
      refetchOnMount: false,       // El staleTime decide si refetch en mount
      retry: 1,
    },
  },
});

// Banner sticky que aparece SOLO si la sesión de Cloudflare Access expiró y NO
// fue posible recuperarla en silencio (ver expire() abajo). En uso normal el
// heartbeat mantiene la cookie viva y, si caduca tras un suspend, recargamos
// transparentemente → este banner sólo se ve si estabas escribiendo algo.
function SessionExpiredBanner({ onReload }) {
  return (
    <div style={{
      position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 10000,
      maxWidth: 'calc(100vw - 32px)',
      background: 'rgba(20,22,28,0.92)', color: '#f4f4f5',
      backdropFilter: 'saturate(160%) blur(8px)', WebkitBackdropFilter: 'saturate(160%) blur(8px)',
      padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14,
      fontFamily: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', fontSize: 13.5,
      border: '1px solid rgba(255,255,255,0.10)', borderRadius: 12,
      boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
      animation: 'cmBannerIn .28s cubic-bezier(.2,.8,.2,1)',
    }}>
      <style>{'@keyframes cmBannerIn{from{opacity:0;transform:translate(-50%,-10px)}to{opacity:1;transform:translate(-50%,0)}}'}</style>
      <span style={{ fontSize: 16, lineHeight: 1 }}>🔄</span>
      <span style={{ opacity: 0.92 }}>
        Tu sesión caducó por inactividad. Tus cambios siguen aquí — recarga para continuar.
      </span>
      <button
        type="button"
        onClick={onReload}
        style={{
          marginLeft: 4, padding: '7px 16px',
          background: '#fff', color: '#16181c',
          border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Actualizar
      </button>
    </div>
  );
}

// ¿Es seguro recargar la página sin avisar? Sólo si el usuario no está
// escribiendo (campo enfocado) ni hay trabajo marcado como sin guardar.
// Fallback mientras baja el chunk de la ruta (muy ligero: un spinner centrado, sin
// dependencias pesadas, para que /vende en el iframe no parpadee algo aparatoso).
function RouteFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
        width: 28, height: 28, borderRadius: '50%',
        border: '3px solid rgba(0,0,0,0.12)', borderTopColor: 'rgba(0,0,0,0.5)',
        animation: 'cmSpin .7s linear infinite',
      }} />
      <style>{'@keyframes cmSpin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
}

function isSafeToAutoReload() {
  const el = typeof document !== 'undefined' ? document.activeElement : null;
  if (el) {
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return false;
    if (el.isContentEditable) return false;
  }
  if (typeof window !== 'undefined' && window.__dashboardDirty) return false;
  return true;
}

export default function App() {
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const expire = () => {
      // Recuperación silenciosa: si la sesión del IdP sigue viva, recargar
      // re-autentica en CF Access sin que el usuario vea nada. Sólo lo hacemos
      // si es seguro (no está escribiendo) y no acabamos de intentarlo (anti-bucle).
      let last = 0;
      try { last = Number(sessionStorage.getItem('cf-auto-reload-ts') || 0); } catch {}
      const now = Date.now();
      if (isSafeToAutoReload() && now - last > 60000) {
        try { sessionStorage.setItem('cf-auto-reload-ts', String(now)); } catch {}
        window.location.reload();
        return;
      }
      setSessionExpired(true);
    };
    const stop = startSessionHeartbeat({ onExpired: expire });
    const off = onSessionExpired(expire);
    return () => { stop(); off(); };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {sessionExpired && <SessionExpiredBanner onReload={() => window.location.reload()} />}
      <Router>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Root - Dashboard principal */}
          <Route path="/" element={<Dashboard />} />

          {/* Market Analysis Dashboard - Standalone */}
          <Route path="/market-analysis" element={<MarketAnalysisDashboard />} />

          {/* Analog Cameras Dashboard - Standalone */}
          <Route path="/analog-cameras" element={<AnalogCamerasDashboard />} />

          {/* Google Ads Dashboard - Standalone */}
          <Route path="/google-ads" element={<GoogleAdsDashboard />} />

          {/* Formulario de venta "Vende tu equipo" — vista del flujo (también se
              sirve como bundle standalone vía src/sell.js en el theme) */}
          <Route path="/vende" element={<SellForm />} />

          {/* Tasador público "¿Cuánto vale tu cámara?" — lead magnet.
              /tasa/:slug = deep-link por modelo desde las landings SEO. */}
          <Route path="/tasa" element={<Tasador />} />
          <Route path="/tasa/:slug" element={<Tasador />} />

          {/* Comprobador de objetivos compatibles — lead magnet/herramienta. */}
          <Route path="/objetivos" element={<LensCompat />} />
          <Route path="/objetivos/:slug" element={<LensCompat />} />
        </Routes>
        </Suspense>
      </Router>
    </QueryClientProvider>
  );
}
